import * as React from 'react'

import type { CommentData } from '../../fragments/commentData'

import {
  useEngagementCoordinationStore,
  useEngagementCoordinationStoreApi,
} from '../../../context/EngagementCoordinationContext'
import { buildCoordinationResetKeys } from '../../../hooks/engagement/buildCoordinationResetKeys'
import {
  deriveAllResolved,
  resolveNextExpansion,
  selectLocallyResolved,
  selectLocallyUnresolved,
} from '../../../hooks/engagement/engagementCoordinationStore'
import type { CoordinationItem } from '../../../hooks/engagement/engagementCoordinationStore'
import { useEngagementListCoordination } from '../../../hooks/engagement/useEngagementListCoordination'
import type { BaseCommunityFilters } from '../../../CommunityFilters/types'
import { subscribeToCommentResolvedEvents } from '../utils/commentResolutionEvents'
import { scrollToAndExpand } from '../utils/scrollToAndExpand'

type UseCommentListCoordinationParams = {
  comments: CommentData[]
  channelIds: string[]
  filters?: BaseCommunityFilters
  /** Whether the comments query is still fetching; see the driver's `isLoading`. */
  isLoading?: boolean
}

type UseCommentListCoordinationReturn = {
  /** Manually expand a card, or collapse with `null`. */
  expand: (commentId: string | null) => void
  /**
   * Expand the next card. Comment-specific: scrolls the target into view before
   * expanding (the store's next-card maths is shared via `resolveNextExpansion`;
   * the scroll is comment presentation).
   */
  expandNext: (fromCommentId?: string, onlyRepliable?: boolean) => void
  expandPrevious: (fromCommentId?: string) => void
  collapse: () => void
  setResolution: (commentId: string, isResolved: boolean) => void
  /** Every visible comment is resolved this session — drives the empty state. */
  allLocallyResolved: boolean
  /** A card was explicitly un-resolved this session (e.g. undo dismiss). */
  hasLocallyUnresolved: boolean
}

/**
 * Comments adapter over the shared coordination driver. Owns the comment
 * candidacy rule (unanswered & not resolved this session), the comment-only
 * scroll-and-expand on advance, and the resolution-derived empty-state flags;
 * delegates the navigation/reset machinery to `useEngagementListCoordination`.
 */
export const useCommentListCoordination = ({
  comments,
  channelIds,
  filters,
  isLoading = false,
}: UseCommentListCoordinationParams): UseCommentListCoordinationReturn => {
  const store = useEngagementCoordinationStoreApi()
  const locallyResolved = useEngagementCoordinationStore(selectLocallyResolved)
  const locallyUnresolved = useEngagementCoordinationStore(
    selectLocallyUnresolved,
  )

  // A comment is intrinsically repliable while it's still unanswered; the store
  // subtracts this session's resolutions to derive the live candidacy, so this
  // no longer depends on `locallyResolved` (and the order stops churning on
  // every resolution).
  const coordinationItems = React.useMemo<CoordinationItem[]>(
    () =>
      comments.map((comment) => ({
        id: comment.id,
        isRepliable: comment.status === 'unreplied' || comment.status === 'new',
      })),
    [comments],
  )

  // Unanswered opens the first unanswered card; All (the default when no
  // status is set) opens the top card only if it's still unanswered.
  const isAllFilter = !filters?.status || filters.status === 'all'
  const shouldAutoExpand =
    filters?.status === 'unreplied' || filters?.status === 'new' || isAllFilter
  const { resetExpansionKey, resetResolutionKey } = buildCoordinationResetKeys(
    filters,
    channelIds,
  )

  useEngagementListCoordination({
    items: coordinationItems,
    shouldAutoExpand,
    autoExpandTopOnly: isAllFilter,
    isLoading,
    resetExpansionKey,
    resetResolutionKey,
    cardWrapperSelector: '[data-comment-wrapper="true"]',
  })

  // Replies sent outside this list's subtree (the comment-details dialog)
  // announce themselves through a module-level event; mark the card resolved
  // this session so it greys exactly like a card-originated reply. Ids not in
  // this list are harmless — the sets reset with the usual resolution keys.
  React.useEffect(
    () =>
      subscribeToCommentResolvedEvents((commentId) => {
        store.getState().setResolution(commentId, true)
      }),
    [store],
  )

  // Whether every visible comment is resolved this session. Needs each
  // comment's server status, so it's derived here rather than in the store.
  const allLocallyResolved = React.useMemo(
    () =>
      deriveAllResolved(
        comments.map((comment) => ({
          id: comment.id,
          isResolvedOnServer:
            comment.status === 'replied' || comment.status === 'dismissed',
        })),
        locallyResolved,
        locallyUnresolved,
      ),
    [comments, locallyResolved, locallyUnresolved],
  )
  const hasLocallyUnresolved = locallyUnresolved.size > 0

  // Comments scroll the next card into view before expanding it (a long list of
  // tall cards); mentions advance immediately and rely on textarea-focus scroll.
  const isMountedRef = React.useRef(true)
  const scrollCleanupRef = React.useRef<(() => void) | null>(null)
  React.useEffect(() => {
    isMountedRef.current = true
    return (): void => {
      isMountedRef.current = false
      scrollCleanupRef.current?.()
    }
  }, [])

  const expandNext = React.useCallback(
    (fromCommentId?: string, onlyRepliable = false): void => {
      const {
        order,
        expandedId,
        locallyResolved: stateLocallyResolved,
      } = store.getState()
      const result = resolveNextExpansion(
        order,
        fromCommentId ?? expandedId,
        onlyRepliable,
        stateLocallyResolved,
      )
      if (result.type === 'noop') return
      if (result.type === 'collapse') {
        store.getState().collapse()
        return
      }
      scrollCleanupRef.current?.()
      scrollCleanupRef.current = scrollToAndExpand(
        `[data-comment-id="${result.id}"]`,
        () => store.getState().expand(result.id),
        isMountedRef,
      )
    },
    [store],
  )

  const { expand, expandPrevious, collapse, setResolution } = store.getState()

  return {
    expand,
    expandNext,
    expandPrevious,
    collapse,
    setResolution,
    allLocallyResolved,
    hasLocallyUnresolved,
  }
}
